import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { DebugMode } from '@smart-builder/components/src/base/DebugMode';
// SBAppContainer is used by login-rest, so needs to be as lightweight as possible.
//  Use direct imports instead of bringing in all of @smart-builder/components
import { useDialog } from '@smart-builder/components/src/dialog/hooks';
import { DialogAction } from '@smart-builder/types';
import { SBBuild } from '@smart-builder/util';
import {
  apiLogin,
  callAPI,
  getXtiId,
  navigateToLoginApp,
} from '@smart-builder/rest';
import { DialogType } from '@smart-builder/components';

type SBAppContainerProps = {
  appName: string;
  queryParams?: any; // any query params to pass into the /apps/ request - used when the translations would depend on query params
};
export const SBAppContainer: React.FC<
  PropsWithChildren<SBAppContainerProps>
> = ({ appName, queryParams, children }) => {
  const [result, setResult] = useState<any>(undefined);
  const { dialogOk, dialogCustom } = useDialog();

  useEffect(() => {
    (async () => {
      if (process.env.NODE_ENV === 'development' && SB_CONFIG.bypassDLL) {
        // SS 20230110 The bypassDLL setting allows the plus-design-test app to load scenes & scene textures
        // without the need for a DLL. No need to continue here.
        setResult(true);
        return;
      }

      try {
        const { minor, major } = SBBuild;

        const body = { xtiId: getXtiId() ?? 0 }; // grab xti from query param or settings.js
        const { result } = await callAPI(`/apps/${appName}/`, 'GET', {
          body,
          queryParams: {
            minorVersion: minor,
            majorVersion: major,
            ...queryParams,
          },
        });

        // assign all values of result to SB_CONFIG
        Object.entries(result).forEach(
          ([k, v]) => ((SB_CONFIG as Record<any, any>)[k] = v),
        );
        SB_CONFIG.appName = appName;

        if (!result.sessionValid && result.loginURL) {
          if (process.env.NODE_ENV === 'production') {
            // no valid session atm, immediately redirect to the login screen
            navigateToLoginApp(true);
            return;
          } else {
            // in debug, allow credentials to be provided via settings.js
            const { DEBUG } = SB_CONFIG as any;
            if (DEBUG) {
              const { username, password } = DEBUG;
              console.info('logging in as', username);
              await apiLogin(result.xtiId, username, password);
            } else {
              // It's useful to debug the login too ya'll
              navigateToLoginApp(true);
              return;
            }
          }
        }

        setResult(result);
      } catch (e) {
        // can't use translations here - the request that fetches translations is the one that has failed

        console.warn(e);
        await dialogOk({
          title: 'Error',
          message: e.errorMessage ?? 'Something went wrong, Please try again.',
          dialogType: DialogType.Error,
        });

        window.location.reload();
      }
    })();
  }, [appName, dialogOk, queryParams]);

  useEffect(() => {
    (window as any).showSBDebug = () => {
      dialogCustom(<DebugMode />, [DialogAction.Ok], {
        closeOnBackground: false,
      }).then(() => window.location.reload()); // need to reload for logging changes to be picked up
    };
  });

  return result ? <>{children}</> : <Spinner animation="border" />;
};
